'use client';

import { useMemo } from 'react';
import { useInView } from 'react-intersection-observer';

import classNames from 'classnames';

import { DEFAULT_GET_REWARD_POOL_LIMIT } from '@shared/api/rewardPools';
import type { RewardPoolDistributionPolicy } from '@shared/api/types/rewardPools';
import { useRewardPoolsInfinityQuery } from '@shared/hooks/query/rewardPools';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

import {
  RewardPoolCard,
  rewardPoolCardHeightClassName,
  rewardPoolCardRootBaseClassName,
} from '../../common/RewardPoolCard/RewardPoolCard';
import { SegmentContainer } from '../SegmentContainer';
import {
  CategoryFilter,
  REWARD_POOLS_CATEGORY_ALL_FILTER,
  useRewardPoolsCategoryFilterQueryState,
} from './filters/CategoryFilter';
import {
  DistributionPolicyFilter,
  useRewardPoolsDistributionPolicyFilterQueryState,
} from './filters/DistributionPolicyFilter';
import { FiltersDialog } from './filters/FiltersDialog';
import { OrderByFilter, useRewardPoolsOrderByFilterQueryState } from './filters/OrderByFilter';
import { StatusFilter, useRewardPoolsStatusFilterQueryState } from './filters/StatusFilter';

export const AllPools = () => {
  const [status] = useRewardPoolsStatusFilterQueryState();
  const [category] = useRewardPoolsCategoryFilterQueryState();
  const [orderBy] = useRewardPoolsOrderByFilterQueryState();
  const [distributionPolicies] = useRewardPoolsDistributionPolicyFilterQueryState();

  const {
    data: rewardPools,
    isLoading,
    isError,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    error,
    refetch,
  } = useRewardPoolsInfinityQuery({
    mode: status,
    tags: category === REWARD_POOLS_CATEGORY_ALL_FILTER ? [] : [category],
    orderBy,
    distributionPolicies: distributionPolicies.includes(REWARD_POOLS_CATEGORY_ALL_FILTER)
      ? []
      : (distributionPolicies as RewardPoolDistributionPolicy[]),
  });

  const { ref } = useInView({
    onChange: (inView) =>
      inView && !isError && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });

  const allRewardPools = useMemo(
    () => rewardPools?.pages.flatMap((page) => page.pools) || [],
    [rewardPools],
  );
  const isEmpty = !allRewardPools.length;

  const handleTryAgainButtonClick = () => {
    if (isEmpty) {
      refetch();
    } else {
      fetchNextPage();
    }
  };

  const rewardPoolGridClassName =
    'grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

  return (
    <SegmentContainer title="All Pools">
      <div className="mb-8 xl:mb-10">
        <div className="flex gap-8 items-start justify-between max-md:hidden">
          <div className="flex flex-wrap gap-3">
            <StatusFilter />
            <DistributionPolicyFilter />
            <CategoryFilter />
          </div>

          <OrderByFilter statusFilterValue={status} />
        </div>

        <FiltersDialog />
      </div>

      <div
        className={classNames('flex flex-col gap-4 ', rewardPoolCardHeightClassName)}
        data-qa="all_pools"
      >
        {!isEmpty && (
          <ul className={rewardPoolGridClassName}>
            {allRewardPools.map((rewardPool) => {
              return (
                <li key={rewardPool.id}>
                  <RewardPoolCard className="h-full" rewardPool={rewardPool} />
                </li>
              );
            })}
          </ul>
        )}

        <div className="flex flex-col flex-1">
          {!isError && (isLoading || hasNextPage) && (
            <div
              ref={ref}
              // Key is added to trigger in view cb again if component is already in view
              key={rewardPools?.pages.length}
            >
              <ul className={rewardPoolGridClassName}>
                {Array.from({ length: DEFAULT_GET_REWARD_POOL_LIMIT }, (_, i) => {
                  return (
                    <li
                      key={i}
                      className={classNames(rewardPoolCardRootBaseClassName, 'skeleton-block')}
                    />
                  );
                })}
              </ul>
            </div>
          )}

          {!isLoading && !isError && isEmpty && (
            <p className="m-auto text-center text-[#9A9AAC]">Empty</p>
          )}

          {!isLoading && isError && (
            <div className="flex flex-col gap-3 m-auto">
              <p className="text-center text-secondary-600">
                Error happened while loading:
                <br />
                {getErrorMessage(error)}
              </p>
              <button
                className="msq-btn msq-btn-md msq-btn-primary mx-auto"
                onClick={handleTryAgainButtonClick}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </SegmentContainer>
  );
};
